import { isAxiosError } from 'axios'
import { ItemCache } from '@/utilities/cache'
import { portalApiClient, withAuthHeaders } from '@/platform/http'
import { nextApiClient } from '@/platform/http/nextApiClient'
import { getIdentity } from '@/services/shared/authRepository'
import { setPostSignupAuthUserIdBridge } from '@/utilities/auth/postSignupAuthUserIdBridge'
import { getStytchJWT } from '@/utilities/stytch/getStytchJWT'
import { isImpersonatedStytchPayload } from '@/utilities/shared/stytchSession'
import {
  HipaaConsentSchemaType,
  UpdateUserDTOType,
  UpdateEmailDTOType,
  PatientSignUpStytchDTOType,
  PatientUserModelExtendedSchemaType,
  RegisterPatientUserWithKitAndHipaaConsentDTOType,
  RegisterPatientUserWithKitAndHipaaConsentResponseSchemaType,
} from '@/types/portal-api.generated'
import type { HipaaSessionInitResponseType } from '@/types/next-api.generated'

const getIdentityInfo = () => getIdentity()

const fetchUserDiscovery = async (email: string, organizationId: string) =>
  portalApiClient.discover_user_in_auth_admin_v2_auth_user_discovery_post(
    { email, organization_id: organizationId },
    withAuthHeaders({}),
  )

const userCache = new ItemCache<Promise<ProcessedUser>>({ ttl: 60_000 })

export type ProcessedUser = PatientUserModelExtendedSchemaType & {
  name: string
  phone: string
  formattedSexAtBirth: string
  day: string | undefined
  month: string | undefined
  year: string | undefined
  dateOfBirth: {
    month: string | undefined
    day: string | undefined
    year: string | undefined
    dateOfBirth: string
  }
}

const processUser = (data: PatientUserModelExtendedSchemaType): ProcessedUser => {
  const dob = data.dob.split('-')
  const year = dob[0]
  const month = dob[1]?.startsWith('0') ? dob[1].slice(1) : dob[1]
  const day = dob[2]?.startsWith('0') ? dob[2].slice(1) : dob[2]

  const dateOfBirth = {
    month,
    day,
    year,
    dateOfBirth: data.dob,
  }

  return {
    ...data,
    name: `${data.first_name} ${data.last_name}`.trim(),
    phone: data.phone?.replace(/\D/g, '') || '',
    formattedSexAtBirth:
      data?.sex_assigned_at_birth?.charAt(0).toUpperCase() +
        data?.sex_assigned_at_birth?.slice(1) || '',
    sex_assigned_at_birth: data?.sex_assigned_at_birth || '',
    dob: data.dob,
    day,
    month,
    year,
    dateOfBirth,
  }
}

const getProfile = async (bypassCache = false): Promise<ProcessedUser> => {
  if (!bypassCache && userCache.value) {
    return userCache.value
  }

  const process = portalApiClient.profile_v2_users_me_get().then((data) => processUser(data))
  userCache.value = process

  return process
}

const seedPostSignupBridgeFromUserId = (userId: string | null | undefined) => {
  const memberId = getStytchJWT()?.sub?.trim()
  const authUserId = userId?.trim()
  if (memberId && authUserId) {
    setPostSignupAuthUserIdBridge({ memberId, authUserId })
  }
}

const register = async (data: PatientSignUpStytchDTOType) => {
  const response = await portalApiClient.patient_sign_up_v3_auth_sign_up_post(
    data,
    withAuthHeaders({ queries: { send_email: true } }),
  )

  seedPostSignupBridgeFromUserId(response.user_id)
  userCache.clear()
  return response
}

/**
 * Atomically sign up a patient, register their kit, and save HIPAA consent.
 * Prefer this over separate register / registerKit / saveHipaaConsent calls for
 * new-user kit registration so a mid-flow failure cannot leave a split state.
 */
const assertNotImpersonatedConsentMutation = (action: string) => {
  if (isImpersonatedStytchPayload(getStytchJWT())) {
    throw new Error(`Cannot ${action} for an impersonated session`)
  }
}

const registerWithKitAndHipaaConsent = async (
  data: RegisterPatientUserWithKitAndHipaaConsentDTOType,
): Promise<RegisterPatientUserWithKitAndHipaaConsentResponseSchemaType> => {
  assertNotImpersonatedConsentMutation('register with kit and HIPAA consent')

  const response =
    await portalApiClient.patient_sign_up_with_kit_and_hipaa_consent_v3_auth_sign_up_with_kit_and_hipaa_consent_post(
      data,
      withAuthHeaders({}),
    )

  seedPostSignupBridgeFromUserId(response.user.user_id)
  userCache.clear()
  // Pending-claim cookie cleanup is best-effort. The combined signup already
  // persisted user + kit + HIPAA consent; do not surface cookie failures as
  // registration failures (sequential saveHipaaConsent isolates this similarly).
  try {
    await updateHipaaConsentPendingClaim(false)
  } catch (err) {
    console.warn('Failed to clear HIPAA consent pending claim after combined registration:', err)
  }
  return response
}

const updateProfile = async (data: UpdateUserDTOType) => {
  await portalApiClient.update_profile_v2_users_me_patch(data)
  userCache.clear()
}

const updateEmail = async (data: UpdateEmailDTOType) => {
  await portalApiClient.update_email_v2_auth_update_email_patch(data)
  userCache.clear()
}

const deleteAccount = async () => {
  await portalApiClient.delete_patient_user_v2_users_me_delete(undefined)
  userCache.clear()
}

const HIPAA_SESSION_INIT_STYTCH_SOFT_FAIL_REASON = 'stytch_error'

const isHipaaSessionInitResponse = (data: unknown): data is HipaaSessionInitResponseType =>
  typeof data === 'object' && data !== null && 'ok' in data

const hipaaSessionInitHttpFailureMessage = (error: unknown): string => {
  if (!isAxiosError(error)) {
    return error instanceof Error ? error.message : ''
  }

  const status = error.response?.status
  const statusText = error.response?.statusText ?? error.message
  return status == null ? statusText : `${status} ${statusText}`
}

const updateHipaaConsentPendingClaim = async (pending: boolean): Promise<void> => {
  let data: HipaaSessionInitResponseType
  try {
    data = await nextApiClient.post_hipaa_session_init({ pending })
  } catch (error) {
    const responseData = isAxiosError(error) ? error.response?.data : undefined
    if (isAxiosError(error) && isHipaaSessionInitResponse(responseData)) {
      data = responseData
    } else {
      throw new Error(
        `Failed to update HIPAA consent pending claim: ${hipaaSessionInitHttpFailureMessage(error)}`,
      )
    }
  }

  if (!data.ok) {
    if (data.reason === HIPAA_SESSION_INIT_STYTCH_SOFT_FAIL_REASON) {
      // Route returns HTTP 200 with this reason on Stytch outages so login/flow are not blocked.
      return
    }

    const reasonSuffix = data.reason ? `: ${data.reason}` : ''
    throw new Error(`Failed to update HIPAA consent pending claim${reasonSuffix}`)
  }

  userCache.clear()
}

const saveHipaaConsent = async (params?: {
  versionId?: number | null
}): Promise<HipaaConsentSchemaType> => {
  assertNotImpersonatedConsentMutation('save HIPAA consent')

  const consent = await portalApiClient.save_hipaa_consent_v2_users_me_hipaa_consent_post({
    ...(params?.versionId == null ? {} : { version_id: params.versionId }),
  })
  await updateHipaaConsentPendingClaim(false)
  return consent
}

const userService = {
  deleteAccount,
  fetchUserDiscovery,
  getIdentityInfo,
  getProfile,
  register,
  registerWithKitAndHipaaConsent,
  saveHipaaConsent,
  updateEmail,
  updateHipaaConsentPendingClaim,
  updateProfile,
}

export default userService
